/**
 * Cross-app URL resolution.
 *
 * The four PazPortal apps are siblings on the same domain:
 *   customer-portal → portal.paznetworks.com   (dev :3000)
 *   admin           → admin.paznetworks.com    (dev :3001)
 *   onsite          → onsite.paznetworks.com   (dev :3002)
 *   estimator       → estimate.paznetworks.com (dev :3003)
 *
 * `resolveAppUrl` builds an absolute URL to another app from wherever the
 * current page is running:
 *   1. an explicit `override` origin (e.g. VITE_ADMIN_URL) always wins;
 *   2. localhost / 127.0.0.1 / ::1 / *.localhost → same host, the app's dev port;
 *   3. a host whose first label is one of the known prefixes → swap the
 *      label, keep the rest (so admin.staging.paznetworks.com → portal.staging…);
 *   4. anything else (Vercel preview hosts, no window) → production.
 *
 * Pure and React-free so it can be unit-tested under node.
 */

export type PazApp = 'customer-portal' | 'admin' | 'onsite' | 'estimator';

export const PAZ_APPS: readonly PazApp[] = ['customer-portal', 'admin', 'onsite', 'estimator'];

export const APP_HOST_PREFIX: Record<PazApp, string> = {
  'customer-portal': 'portal',
  admin: 'admin',
  onsite: 'onsite',
  estimator: 'estimate',
};

export const APP_DEV_PORT: Record<PazApp, number> = {
  'customer-portal': 3000,
  admin: 3001,
  onsite: 3002,
  estimator: 3003,
};

export const PRODUCTION_DOMAIN = 'paznetworks.com';

export interface AppUrlLocation {
  protocol: string;
  hostname: string;
  port: string;
}

export interface ResolveAppUrlOptions {
  /** Explicit origin for the target app (e.g. from an env var). Wins over everything. */
  override?: string | null;
  /** Location to derive siblings from. Defaults to window.location when available. */
  location?: AppUrlLocation | null;
}

const LOCAL_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0']);

export function isLocalHostname(hostname: string): boolean {
  const h = hostname.toLowerCase();
  return LOCAL_HOSTS.has(h) || h.endsWith('.localhost');
}

function normalisePath(path: string): string {
  if (!path) return '/';
  return path.startsWith('/') ? path : `/${path}`;
}

function normaliseOrigin(origin: string): string {
  const trimmed = origin.trim().replace(/\/+$/, '');
  return /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

function currentLocation(): AppUrlLocation | null {
  if (typeof window === 'undefined' || !window.location) return null;
  const { protocol, hostname, port } = window.location;
  return { protocol, hostname, port };
}

export function productionAppOrigin(app: PazApp): string {
  return `https://${APP_HOST_PREFIX[app]}.${PRODUCTION_DOMAIN}`;
}

export function resolveAppUrl(
  app: PazApp,
  path: string,
  opts: ResolveAppUrlOptions = {},
): string {
  const target = normalisePath(path);

  if (opts.override && opts.override.trim()) {
    return `${normaliseOrigin(opts.override)}${target}`;
  }

  const loc = opts.location === undefined ? currentLocation() : opts.location;
  if (!loc || !loc.hostname) {
    return `${productionAppOrigin(app)}${target}`;
  }

  const protocol = loc.protocol === 'http:' || loc.protocol === 'https:' ? loc.protocol : 'https:';

  if (isLocalHostname(loc.hostname)) {
    return `${protocol}//${loc.hostname}:${APP_DEV_PORT[app]}${target}`;
  }

  const labels = loc.hostname.toLowerCase().split('.');
  const knownPrefixes = new Set<string>(Object.values(APP_HOST_PREFIX));
  if (labels.length >= 2 && knownPrefixes.has(labels[0])) {
    labels[0] = APP_HOST_PREFIX[app];
    const port = loc.port ? `:${loc.port}` : '';
    return `${protocol}//${labels.join('.')}${port}${target}`;
  }

  return `${productionAppOrigin(app)}${target}`;
}
